import type { DetailsValues } from './detailsValues';

import { isoToEpochDay } from '@/lib/epochDay';

/**
 * The cards' view-mode rules (docs/briefs/project-details-v2 §3), pure so every
 * note and number is assertable. Dates are calendar days: epoch-day arithmetic,
 * never a local `Date` (CLAUDE.md).
 */

/** Who can log time's note: still the level the project started on, or not. */
export const scopeNote = (
  values: Pick<DetailsValues, 'logTimeScope'>,
  defaults: Pick<DetailsValues, 'logTimeScope'>,
): 'default' | 'changed' => (values.logTimeScope === defaults.logTimeScope ? 'default' : 'changed');

/** When's note: the dates are still the ones the project was created with. */
export const isFrameFromCreation = (
  values: Pick<DetailsValues, 'startDate' | 'endDate'>,
  defaults: Pick<DetailsValues, 'startDate' | 'endDate'>,
): boolean =>
  values.startDate !== '' &&
  values.startDate === defaults.startDate &&
  values.endDate === defaults.endDate;

export type ProjectDuration = { unit: 'days' | 'months'; count: number };

/** Average days per month, so four calendar months read as "4 months". */
const DAYS_PER_MONTH = 30.44;

/**
 * "· 4 months" after the range: days under 45, months from there. `null`
 * while either date is missing or the end comes first.
 */
export const projectDuration = (startIso: string, endIso: string): ProjectDuration | null => {
  const start = isoToEpochDay(startIso);
  const end = isoToEpochDay(endIso);
  if (start === null || end === null || end < start) return null;
  const days = end - start;
  if (days < 45) return { unit: 'days', count: days };
  return { unit: 'months', count: Math.max(1, Math.round(days / DAYS_PER_MONTH)) };
};

/**
 * The When card's bar (brief §3; Mette, 1 Nov: today and the delivery date are
 * always marked). The bar spans the project's frame and stretches to take in
 * today and the delivery date when they fall outside it, so both always have
 * a place. Every position is a percentage of that span.
 */
export type FrameTimeline = {
  /** Where the frame starts and ends on the bar. */
  frame: { left: number; right: number };
  /** How far the elapsed part of the frame reaches — start to today, within the frame. */
  elapsedRight: number;
  today: number;
  /** `null` with no delivery date. */
  delivery: number | null;
};

export const frameTimeline = (
  startIso: string,
  endIso: string,
  todayIso: string,
  deliveryIso: string,
): FrameTimeline | null => {
  const start = isoToEpochDay(startIso);
  const end = isoToEpochDay(endIso);
  const today = isoToEpochDay(todayIso);
  const delivery = isoToEpochDay(deliveryIso);
  if (start === null || end === null || today === null || end <= start) return null;
  const first = Math.min(start, today, delivery ?? start);
  const last = Math.max(end, today, delivery ?? end);
  const at = (day: number) => ((day - first) / (last - first)) * 100;
  return {
    frame: { left: at(start), right: at(end) },
    elapsedRight: at(Math.min(Math.max(today, start), end)),
    today: at(today),
    delivery: delivery === null ? null : at(delivery),
  };
};

/**
 * The description is HTML from the editor (slice 5, ADR 0052). Text saved
 * before that — or seeded as plain lines — has no tags: each line becomes a
 * paragraph, escaped, so the editor and the view read it the same way.
 */
export const toEditorHtml = (stored: string): string => {
  if (stored.trim() === '') return '';
  if (/^\s*</.test(stored)) return stored;
  const escape = (line: string) =>
    line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return stored
    .split('\n')
    .map((line) => `<p>${escape(line)}</p>`)
    .join('');
};

/** The words in a description, tags and entities aside — for "is there one?". */
export const descriptionText = (stored: string): string =>
  stored
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** What it's about shows its empty state only when nothing at all is there. */
export const isAboutEmpty = (
  values: Pick<DetailsValues, 'description' | 'files' | 'links'>,
): boolean =>
  descriptionText(values.description) === '' &&
  values.files.length === 0 &&
  values.links.length === 0;

/** "240 KB" / "1.2 MB" — a file chip's size. */
export const formatFileSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${String(Math.max(1, Math.round(bytes / 1024)))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
